import React, { useEffect, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Skeleton,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { CheckIcon } from "@chakra-ui/icons";
import { FiSend, FiUsers } from "react-icons/fi";
import units from "../../utils/units";
import { useQuery } from "react-query";
import { searchWorkersApi } from "../../api/workers";

const Forward = ({
  forwardModal,
  onForward,
  heading = "Forward to staff",
  description = "Choose the staff member who should receive this item.",
  isLoading = false,
}) => {
  const [staff, setStaff] = useState(null);
  const [unitName, setUnitName] = useState("");
  const [subUnit, setSubUnit] = useState("");
  const selectedUnit = units.find((unit) => unit.unit === unitName);

  useEffect(() => setStaff(null), [unitName, subUnit]);
  useEffect(() => {
    if (!forwardModal?.isOpen) {
      setStaff(null);
      setUnitName("");
      setSubUnit("");
    }
  }, [forwardModal?.isOpen]);

  const workersQuery = useQuery(
    ["searchWorkersApi", unitName, subUnit],
    () => searchWorkersApi({ unit: unitName, ...(subUnit ? { subUnit } : {}) }),
    { enabled: Boolean(forwardModal?.isOpen && unitName), keepPreviousData: false },
  );
  const workers = (workersQuery.data?.data || []).filter((worker) => {
    const normalize = (value) => String(value || "").trim().replace(/\s+/g, " ").toLowerCase();
    return normalize(worker.unit) === normalize(unitName)
      && (!subUnit || normalize(worker.subUnit) === normalize(subUnit));
  });

  const closeModal = () => {
    if (isLoading) return;
    setStaff(null);
    setUnitName("");
    setSubUnit("");
    forwardModal?.onClose();
  };

  const submit = () => {
    if (staff) onForward(staff._id);
  };

  return (
    <Modal isCentered size="xl" isOpen={forwardModal?.isOpen} onClose={closeModal} scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="2xl" overflow="hidden">
        <ModalHeader borderBottomWidth="1px" py="5">
          <Heading size="md">{heading}</Heading>
          <Text mt="1" fontSize="sm" color="gray.500" fontWeight="normal">{description}</Text>
        </ModalHeader>
        <ModalCloseButton top="5" />

        <ModalBody py="5">
          <Stack spacing="5">
            <Stack direction={{ base: "column", md: "row" }} spacing="4">
              <FormControl>
                <FormLabel fontSize="sm">Unit</FormLabel>
                <Select
                  value={unitName}
                  onChange={(event) => { setUnitName(event.target.value); setSubUnit(""); }}
                  placeholder="Choose a unit"
                  bg="white"
                >
                  {units.map((unit) => <option key={unit.unit} value={unit.unit}>{unit.unit}</option>)}
                </Select>
              </FormControl>
              <FormControl isDisabled={!unitName}>
                <FormLabel fontSize="sm">Sub-unit <Text as="span" color="gray.400" fontWeight="normal">(optional)</Text></FormLabel>
                <Select value={subUnit} onChange={(event) => setSubUnit(event.target.value)} placeholder="All sub-units" bg="white">
                  {(selectedUnit?.subUnits || []).map((item) => <option key={item} value={item}>{item}</option>)}
                </Select>
              </FormControl>
            </Stack>

            {!unitName ? (
              <Center minH="210px" flexDirection="column" textAlign="center" bg="gray.50" borderRadius="xl" borderWidth="1px" borderStyle="dashed">
                <Icon as={FiUsers} boxSize="7" color="gray.400" />
                <Text mt="3" fontWeight="700">Select a unit to find staff</Text>
                <Text mt="1" fontSize="sm" color="gray.500">You can narrow the list further by choosing a sub-unit.</Text>
              </Center>
            ) : workersQuery.isLoading ? (
              <Stack spacing="3"><Skeleton h="76px" borderRadius="xl" /><Skeleton h="76px" borderRadius="xl" /><Skeleton h="76px" borderRadius="xl" /></Stack>
            ) : workersQuery.isError ? (
              <Center minH="210px" flexDirection="column" textAlign="center">
                <Text fontWeight="700">Unable to load staff</Text>
                <Text mt="1" fontSize="sm" color="gray.500">Check your connection and try again.</Text>
                <Button mt="4" size="sm" variant="outline" onClick={() => workersQuery.refetch()}>Try again</Button>
              </Center>
            ) : !workers.length ? (
              <Center minH="210px" flexDirection="column" textAlign="center">
                <Icon as={FiUsers} boxSize="7" color="gray.400" />
                <Text mt="3" fontWeight="700">No staff found</Text>
                <Text mt="1" fontSize="sm" color="gray.500">Try selecting the whole unit or another sub-unit.</Text>
              </Center>
            ) : (
              <Box>
                <Text mb="3" fontSize="sm" color="gray.500">Select one staff member to continue</Text>
                <VStack spacing="3" align="stretch">
                  {workers.map((worker) => {
                    const selected = staff?._id === worker._id;
                    return (
                      <HStack
                        as="button"
                        type="button"
                        key={worker._id}
                        w="full"
                        textAlign="left"
                        p="3"
                        spacing="3"
                        borderWidth="1px"
                        borderColor={selected ? "brand.500" : "gray.200"}
                        bg={selected ? "brand.50" : "white"}
                        borderRadius="xl"
                        onClick={() => setStaff(worker)}
                        _hover={{ borderColor: "brand.300", bg: selected ? "brand.50" : "gray.50" }}
                      >
                        <Avatar size="sm" name={`${worker.firstName} ${worker.lastName}`} src={worker.image} />
                        <Box flex="1" minW="0">
                          <Text mb="1" fontSize="sm" fontWeight="700" color={worker.roomNumber ? "brand.600" : "gray.400"}>
                            {worker.roomNumber ? `Room ${worker.roomNumber.replace(/^room\s*/i, "")}` : "Room not specified"}
                          </Text>
                          <HStack flexWrap="wrap">
                            <Text fontWeight="800" textTransform="capitalize" noOfLines={1}>{worker.firstName} {worker.lastName}</Text>
                            <Badge colorScheme={worker.presence?.isOnline ? "green" : "gray"} borderRadius="full">
                              {worker.presence?.isOnline ? "Online" : "Offline"}
                            </Badge>
                          </HStack>
                          <Text mt="1" fontSize="sm" color="gray.500" noOfLines={1}>{worker.unit}{worker.subUnit ? ` · ${worker.subUnit}` : ""}</Text>
                        </Box>
                        {selected && <Center bg="brand.500" color="white" borderRadius="full" w="24px" h="24px"><CheckIcon boxSize="3" /></Center>}
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter borderTopWidth="1px" gap="3">
          <Button variant="ghost" onClick={closeModal} isDisabled={isLoading}>Cancel</Button>
          <Button leftIcon={<FiSend />} isDisabled={!staff} isLoading={isLoading} loadingText="Sending" onClick={submit}>
            Send to {staff ? staff.firstName : "selected staff"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default Forward;
